import * as React from "react";
import { Chip, ChipProps, Box, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { GampCategory, getGampEntry } from "../gampDictionary";

type Props = {
    category: GampCategory | string | number | null | undefined;
    lang: "pl" | "en";
    size?: ChipProps["size"]; // "small" | "medium"
    withTooltip?: boolean;
};

const PALETTE_KEY: Record<GampCategory, "info" | "success" | "warning" | "error" | "secondary"> = {
    [GampCategory.Category1_Infrastructure]: "info",
    [GampCategory.Category2_Obsolete]: "secondary",
    [GampCategory.Category3_NonConfigured]: "warning",
    [GampCategory.Category4_Configured]: "success",
    [GampCategory.Category5_Custom]: "error",
};

const toGampCategory = (v: Props["category"]): GampCategory | undefined => {
    if (v === null || v === undefined) return undefined;
    const n = Number(v as any);
    if (!Number.isNaN(n) && n >= 1 && n <= 5) return n as GampCategory;
    const s = String(v);
    const map: Record<string, GampCategory> = {
        Category1_Infrastructure: 1,
        Category2_Obsolete: 2,
        Category3_NonConfigured: 3,
        Category4_Configured: 4,
        Category5_Custom: 5,
    };
    return map[s];
};

export const GampCategoryChip: React.FC<Props> = ({
    category,
    lang,
    size = "small",
    withTooltip = true,
}) => {
    const cat = toGampCategory(category);
    if (!cat) return null;

    const t = getGampEntry(cat, lang);
    const paletteKey = PALETTE_KEY[cat] ?? "info";
    const chip = (
        <Chip
            size={size}
            variant="outlined"
            color="default"
            icon={
                <Box
                    sx={(theme) => ({
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: theme.palette[paletteKey].main,
                    })}
                />
            }
            label={t?.label ?? String(cat)}
            sx={(theme) => ({
                bgcolor: alpha(theme.palette[paletteKey].main, theme.palette.mode === "dark" ? 0.16 : 0.1),
                borderColor: alpha(theme.palette[paletteKey].main, 0.5),
                color: theme.palette.text.primary,
                fontWeight: 800,
                letterSpacing: ".02em",
                borderRadius: 1,
                height: 24,
                "& .MuiChip-label": { px: 1, py: 0.25, fontSize: "0.75rem", lineHeight: 1.6 },
                "& .MuiChip-icon": { mr: 0.5 },
            })}
        />
    );

    if (!withTooltip || !t) return chip;

    return (
        <Tooltip
            placement="top"
            title={
                <div style={{ maxWidth: 360 }}>
                    <b>{t.label}</b>
                    <div style={{ marginTop: 6 }}>{t.description}</div>
                    <div style={{ marginTop: 6 }}>
                        <b>{lang === "pl" ? "Przykłady" : "Examples"}:</b> {t.examples}
                    </div>
                    <div style={{ marginTop: 6 }}>
                        <b>{lang === "pl" ? "Poziom walidacji" : "Validation level"}:</b> {t.validationLevel}
                    </div>
                </div>
            }
        >
            <span>{chip}</span>
        </Tooltip>
    );
};
